import React, { useState, useEffect } from 'react';
import VirtualDeviceService from '../services/VirtualDeviceService';
import { Device } from './DeviceCard';
import './VirtualDevicePanel.css';
import ApiService from '../services/ApiService';

interface VirtualDevicePanelProps {
  onDevicesCreated: (devices: Device[]) => void;
}

const VirtualDevicePanel: React.FC<VirtualDevicePanelProps> = ({ onDevicesCreated }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [deviceType, setDeviceType] = useState<'light' | 'thermostat' | 'fan' | 'switch' | 'lock' | 'camera'>('light');
  const [deviceRoom, setDeviceRoom] = useState('Living Room');
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Ensure WebSocket connection is established when component mounts
  useEffect(() => {
    const initializeConnection = async () => {
      try {
        setIsConnecting(true);
        await ApiService.connectWebSocket();
        console.log('WebSocket connection established');
      } catch (error) {
        console.warn('Could not establish WebSocket connection:', error);
        // Non-blocking error - we'll try again when creating a device
      } finally {
        setIsConnecting(false);
      }
    };
    
    initializeConnection();
    
    // Clean up on unmount
    return () => {
      // No need to disconnect as other components might be using the connection
    };
  }, []);
  
  // Demo devices functionality removed
  
  const handleCreateDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!deviceName || !deviceRoom) {
      return;
    }
    
    try {
      setIsCreating(true);
      setError(null);
      
      // Ensure WebSocket connection is established before creating device
      try {
        await ApiService.connectWebSocket();
      } catch (wsError) {
        console.warn('Could not connect to WebSocket, continuing with device creation:', wsError);
      }
      
      // Set initial state based on device type
      let initialState = {};
      
      switch (deviceType) {
        case 'light':
          initialState = { power: 'off', brightness: 80 };
          break;
        case 'thermostat':
          initialState = { power: 'on', temperature: 72, targetTemperature: 70, mode: 'cool' };
          break;
        case 'fan':
          initialState = { power: 'off', speed: 2 };
          break;
        case 'switch':
          initialState = { power: 'off' };
          break;
        case 'lock':
          initialState = { locked: false };
          break;
        case 'camera':
          initialState = { power: 'off', recording: false };
          break;
      }
      
      console.log(`Creating ${deviceType} device "${deviceName}" in ${deviceRoom}`);
      const device = await VirtualDeviceService.createVirtualDevice(
        deviceName,
        deviceType,
        deviceRoom,
        initialState
      );
      
      if (device) {
        console.log('Device created successfully:', device);
        onDevicesCreated([device]);
        
        // Reset form
        setDeviceName('');
        setError(null);
      } else {
        console.error('Device creation returned null');
        setError('Failed to create device - please check console for details');
      }
    } catch (error) {
      console.error('Error creating virtual device:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`Error creating virtual device: ${errorMessage}`);
    } finally {
      setIsCreating(false);
    }
  };
  
  return (
    <div className="virtual-device-panel">
      <h2>Virtual Devices</h2>
      <p>Create virtual devices to test the system without physical hardware.</p>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {/* Removed demo devices button */}
      
      <div className="virtual-device-form-container">
        <h3>Create Custom Virtual Device</h3>
        <form className="virtual-device-form" onSubmit={handleCreateDevice}>
          <div className="form-group">
            <label htmlFor="deviceName">Device Name</label>
            <input
              type="text"
              id="deviceName"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="e.g., Living Room Light"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="deviceType">Device Type</label>
            <select
              id="deviceType"
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value as any)}
              required
            >
              <option value="light">Light</option>
              <option value="thermostat">Thermostat</option>
              <option value="fan">Fan</option>
              <option value="switch">Switch</option>
              <option value="lock">Lock</option>
              <option value="camera">Camera</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="deviceRoom">Room</label>
            <input
              type="text"
              id="deviceRoom"
              value={deviceRoom}
              onChange={(e) => setDeviceRoom(e.target.value)}
              placeholder="e.g., Living Room"
              required
            />
          </div>
          
          <button 
            type="submit" 
            className="create-device-button"
            disabled={isCreating || isConnecting || !deviceName || !deviceRoom}
          >
            {isCreating ? 'Creating...' : isConnecting ? 'Connecting...' : 'Create Device'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default VirtualDevicePanel;